/**
 * #344 — the Game Detail order ticket's orchestration, extracted from
 * `OrderEntryForm` so the component is presentation only.
 *
 * What lives here: the ticket's own state, its effects, the store reads, and
 * the two commands (`submit`, `fireOscar`). What deliberately does NOT live
 * here: pure derivation, which sits in `orderEntryEconomics`,
 * `orderEntrySubmitGate` and `orderEntryPayloads` so it can be tested without
 * a store mock in sight.
 *
 * Three properties this file is responsible for preserving exactly:
 *
 *   1. The four intent-only effects keep their trimmed dependency arrays. They
 *      are triggers, not derivations — widening them re-seeds the price twice
 *      and clobbers a typed qty.
 *   2. `submit()` and `fireOscar()` bump headroom as their LAST statement, not
 *      in a `finally`. Both can throw before their try blocks, and the JSX
 *      `.then(() => bumpHeadroom())` they replace skipped the bump on a throw.
 *   3. `submit()` keeps its own local `priceCents`/`count`, which are NaN on
 *      invalid input where the render-scope ones are 0. The submit guards read
 *      as `isNaN(count)`; handing them a 0 would change which orders are
 *      rejected.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { unsupportedVenue } from '@/lib/venueGuard';
import { beginPolyAttempt } from '../../lib/infra/polyAttemptJournal';
import { bumpHeadroom, useHeadroom, type HeadroomSnapshot } from '../../hooks/useHeadroom';
import { polyPlacementOutcome, ringPolyPlacement } from '../../hooks/usePolymarketRelayOrders';
import { useAccountStore } from '../../stores/accountStore';
import { useAuthStore } from '../../stores/authStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useOneClickStore } from '@/stores/oneClickStore';
import { describeLimitRejection, previewOrder, positionKey, type LimitRejectionBreach } from '../../lib/limits';
import { useFeeFor } from '@/services/hydraFees';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { useOscarAccess } from '@/hooks/useOscarAccess';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { useOscarReceipt } from '@/hooks/useOscarReceipt';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';
import { useOneClickReceipts } from '@/hooks/useOneClickReceipts';
import { useReceiptFillReread } from './useReceiptFillReread';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import {
  kalshiFallbackTickCents,
  kalshiPriceBounds,
  mirrorCents,
  stepKalshiPriceCents,
  tickCentsFor,
  validateKalshiPriceCentsInput,
  validatePriceCentsInput,
  type PriceInputResult,
} from '@/lib/pricing/subCent';
import { POLY_MIN_ORDER_CONTRACTS } from '@/lib/polymarket/closePosition';
import { explainPolyError } from '@/lib/polymarket/errors';
import {
  describePolyRelayUnavailable,
  placePolyOrderViaRelay,
} from '@/lib/polymarket/relay';
import { fetchMinTickSize, resolveTickCents } from '@/lib/polymarket/tickSize';
import {
  computeLadderTotals,
  computeOrderEntryEconomics,
  type LadderTotals,
  type OrderTicketQuote,
} from './orderEntryEconomics';
import {
  buildKalshiOrderPayload,
  buildLadderLegPayload,
  buildOscarSequenceInput,
  buildPolyOrderPayload,
  expirationTimeFrom,
  expirationTsFrom,
  resolveExpiry,
  resolveLadderPlacements,
} from './orderEntryPayloads';
import { minQtyFor, submitGate } from './orderEntrySubmitGate';
import {
  clearTicketExpiry,
  nextTicketExpiryOwner,
  publishTicketExpiry,
} from '@/lib/trading/oneClickExpiry';
import {
  buildLadderLegs,
  summarizeLegs,
  validateLeg,
  MIN_ORDER_SPACING_MS,
  type LadderLeg,
  type LadderSummary,
} from './orderLadder';
import {
  computeOscarPreview,
  loadOscarRetryIdentity,
  oscarRetryIdentity,
  saveOscarRetryIdentity,
  type OscarPreview,
  type OscarReleasePrefill,
  type OscarRetryIdentity,
} from './oscar';
import {
  describeOscarSequenceResult,
  type OrderResultLine,
} from './oscarResultPresentation';
import { describePolyOrderOutcomeLine } from './polyOrderResultPresentation';
import {
  ORDER_UNCONFIRMED_LABEL,
  describeOrderAttempt,
  matchupLabel,
  placementBatchPendingLine,
  placementFailureLine,
  placementPendingLine,
  placementSelfTradePartialLine,
  placementSuccessLine,
} from './orderAttemptIdentity';
import { OrderSubmissionUnknownError, SELF_TRADE_CANCEL_REFERENCE } from '@/lib/trading/orderSubmissionOutcome';
import { resolvePolyOrderTokens } from './polyOrderTokens';
import { limitOrderRole, bestPriceCentsForOrderSide } from './tradeModalBook';
import { hasCfbEventSynthKey, cfbEventOutcomeLabel } from '@/lib/sport/cfbEventSynthKey';
import type { FeePerspective } from './ladder/helpers';
import type { MarketSide, TeamSide } from './types';
import { ticketBook, type OrderEntryTarget } from './orderEntryTarget';
import type { KalshiExchangeIndexState } from '@/lib/kalshiExchangeIndex';
import type { UseOneClickReceiptsResult } from '@/hooks/useOneClickReceipts';
import type { UseOscarReceiptResult } from '@/hooks/useOscarReceipt';

export type { LadderTotals };

/** The ticket's per-venue draft slots (size, duration, custom expiry): what a
 *  venue-tab round trip keeps. A host that unmounts the ticket (Game Detail's
 *  US tab) hands them back on remount so a typed draft is not discarded. */
export interface OrderEntryDraftSlots {
  qtyByVenue: Record<'kalshi' | 'polymarket', string>;
  expiryModeByVenue: Record<'kalshi' | 'polymarket', 'gts' | 'gtc' | 'custom'>;
  customExpiry: string;
}

export interface OrderEntryModelInput {
  game: OrderEntryTarget;
  selectedSide: TeamSide;
  venue: 'kalshi' | 'polymarket';
  orderSide: MarketSide;
  price: string;
  orderType: 'limit' | 'market';
  priceFlashKey: number;
  extraLegs: LadderLeg[];
  oscarReleaseIntent: OscarReleasePrefill | null;
  // The five parent mutators the moved code calls. `onAddLeg`, `onUpdateLeg`
  // and `onOrderTypeChange` are JSX-only and deliberately absent.
  onPriceChange: (next: string) => void;
  onOrderSideChange: (next: MarketSide) => void;
  onClearLegs: () => void;
  onClearOscarReleaseIntent: () => void;
  onRemoveLeg: (id: string) => void;
  /** True while the shared limits (headroom) read is failing, so the breach
   *  line can say its figures are not current. The host reads it (the
   *  headroom hook's failure flag); absent reads as false. */
  limitsReadsFailing?: boolean;
  /** Game Detail's ticket only: publish this ticket's Kalshi expiry selection
   *  so one-click and hotkey presses on that page use the duration the
   *  ticket shows (oneClickExpiry). Absent: nothing is published. */
  publishOneClickExpiry?: boolean;
  /** Draft slots to start from (a host-kept draft); absent: blank size, Settings' durations. */
  draft?: OrderEntryDraftSlots;
  /** Told the current slots whenever they change, so the host can keep them across an unmount. */
  onDraftChange?: (slots: OrderEntryDraftSlots) => void;
}

export interface OrderEntryModel {
  // ── Venue & market context ──
  isKalshiVenue: boolean;
  ticker: string;
  exchangeIndex?: number;
  exchangeIndexState?: KalshiExchangeIndexState;
  teamCode: (team: TeamSide) => string;

  // ── Early-return gates (the returns stay in the component) ──
  accountConnected: boolean;
  /** Kalshi keys are confirmed absent (or not permitted): the ticket shows
   *  the connect message instead of the form. Unknown/failed/connecting
   *  are not absence — the form stays mounted with submit disabled. */
  kalshiAccountAbsent: boolean;
  /** Why new Kalshi orders are unavailable while the account is not
   *  connected (checking, reconnecting, check failed); null when connected
   *  or on Polymarket. Display only — `submitDisabled` is the gate. */
  kalshiAccountReason: string | null;
  /** Present only when the key check failed: re-runs it. */
  retryKalshiAccount: (() => void) | null;
  hasTicker: boolean;
  hasPolyTokens: boolean;

  // ── Price ──
  priceBounds: { min: number; max: number };
  previousPrice: number | null;
  nextPrice: number | null;
  bestYesHint: number | undefined;
  bestNoHint: number | undefined;
  priceFlashing: boolean;
  priceCents: number;

  // ── Quantity ──
  qty: string;
  minQty: number;
  count: number;
  defaultContracts: number;
  quickSizes: number[];

  // ── Expiry ──
  expiryMode: 'gts' | 'gtc' | 'custom';
  gtsAvailable: boolean;
  customExpiry: string;

  // ── Economics ──
  feeLabel: string;
  feeTitle: string;
  totalCostLabel: string;
  maxPayoutLabel: string;
  maxProfit: number | null;
  maxProfitLabel: string;
  effectiveOddsLabel: string;
  /** The Price row's cents + American odds on one basis. Per-contract, and a
   *  different quantity from `effectiveOddsLabel`, which prices the order
   *  total. See {@link OrderTicketQuote}. */
  priceQuote: OrderTicketQuote;

  // ── Limit preview + gate ──
  effectiveBreach: LimitRejectionBreach | null;
  /** The breach as one line for the ticket, or null. While limit reads are
   *  failing it says how old the figures are: "(limits as of 14:02:11)" when
   *  this ticket saw the last read arrive, else "(limits not refreshing)".
   *  The relay still checks the caps on every order. */
  breachLine: string | null;
  marketStale: boolean;
  submitDisabled: boolean;
  submitting: boolean;

  // ── Ladder ──
  ladderActive: boolean;
  ladderSummary: LadderSummary | null;
  ladderTeamSplit: Record<'away' | 'home', number> | null;
  ladderTotals: LadderTotals | null;

  // ── Oscar ──
  oscarAccess: boolean;
  /** The ORDER|OVERTAKE row's place in the ticket; OVERTAKE is usable only
   *  with `oscarAccess`. */
  oscarRowShown: boolean;
  oscarActive: boolean;
  oscarReleaseArmed: boolean;
  blockerOn: boolean;
  blockerQty: string;
  /** Single shared preview — the fire path and the JSX read the same value. */
  oscarPreview: OscarPreview;

  // ── Receipts ──
  showOscarReceipt: boolean;
  showOneClickReceipts: boolean;
  /** One-click grant restored but not yet confirmed (#1070): the receipts
   *  card holds its place as an inert shell; nothing is fetched. */
  oneClickReceiptsPending: boolean;
  oscarReceipt: UseOscarReceiptResult['receipt'];
  oscarReceiptLoading: boolean;
  oscarReceiptError: string | null;
  refreshOscarReceipt: () => Promise<void>;
  oneClickReceipts: UseOneClickReceiptsResult['receipts'];
  oneClickReceiptsLoading: boolean;
  oneClickReceiptsError: string | null;
  refreshOneClickReceipts: () => Promise<void>;

  // ── Result ──
  result: OrderResultLine | null;

  // ── Commands ──
  clearResult: () => void;
  setQty: (next: string | ((prev: string) => string)) => void;
  setCustomExpiry: Dispatch<SetStateAction<string>>;
  setExpiryMode: (next: 'gts' | 'gtc' | 'custom') => void;
  setBlockerOn: Dispatch<SetStateAction<boolean>>;
  setBlockerQty: Dispatch<SetStateAction<string>>;
  enterOscarMode: () => void;
  exitOscarMode: () => void;
  applyPriceStep: (next: number | null) => void;
  applyOrderSide: (next: MarketSide) => void;
  submit: () => Promise<void>;
  fireOscar: () => Promise<void>;
}

/** When a NEW headroom snapshot arrived while this ticket was mounted. The
 *  snapshot present at mount has no known read time (it may be an old cache),
 *  so it records nothing; a later one is a read this ticket saw land. */
function useObservedReadAt(headroom: HeadroomSnapshot | null): number | null {
  const atMountRef = useRef(headroom);
  const [readAtMs, setReadAtMs] = useState<number | null>(null);
  useEffect(() => {
    if (headroom && headroom !== atMountRef.current) setReadAtMs(Date.now());
  }, [headroom]);
  return readAtMs;
}

function clockHms(ms: number): string {
  const d = new Date(ms);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
}

/** The ticket's breach line. Copy matches the cap the preview tripped; the
 *  suffix appears only while limit reads are failing. */
export function breachLineFor(
  breach: LimitRejectionBreach | null,
  readsFailing: boolean,
  readAtMs: number | null,
): string | null {
  if (!breach) return null;
  const base = breach.rule === 'LIMIT_MAX_CONTRACTS'
    ? `Would exceed your contract cap: ${breach.current.toLocaleString()} + ${breach.requested.toLocaleString()} > ${breach.cap.toLocaleString()}`
    : `Would exceed your position cap: ${breach.current.toLocaleString()} + 1 > ${breach.cap.toLocaleString()}`;
  if (!readsFailing) return base;
  return readAtMs != null
    ? `${base} (limits as of ${clockHms(readAtMs)})`
    : `${base} (limits not refreshing)`;
}

export function useOrderEntryModel(input: OrderEntryModelInput): OrderEntryModel {
  const {
    game,
    selectedSide,
    venue,
    orderSide,
    price,
    orderType,
    priceFlashKey,
    extraLegs,
    oscarReleaseIntent,
    onPriceChange,
    onOrderSideChange,
    onClearLegs,
    onClearOscarReleaseIntent,
    onRemoveLeg,
    publishOneClickExpiry = false,
    draft,
    onDraftChange,
  } = input;

  const status = useAccountStore((s) => s.status);
  const credentialsStatus = useAccountStore((s) => s.credentialsStatus);
  const credentialCount = useAccountStore((s) => s.credentialCount);
  const placeOrder = useAccountStore((s) => s.placeOrder);
  const placeOrderSequence = useAccountStore((s) => s.placeOrderSequence);
  const kalshiPositions = useAccountStore((s) => s.positions);
  const headroom = useHeadroom();
  const limitsReadAtMs = useObservedReadAt(headroom);
  const isKalshiVenue = venue === 'kalshi';
  const grantedOscarAccess = useOscarAccess();
  const oscarAccess = grantedOscarAccess && !game.market;
  // Layout only: a restored Oscar grant keeps the ORDER|OVERTAKE row in place,
  // OVERTAKE disabled, until this session confirms it.
  const oscarRowShown = useGrantSlot('oscarAccess') !== 'hidden' && !game.market;

  // User-configurable defaults read from Settings → Preferences. Seed
  // once on mount; mid-session changes to these prefs are intentional
  // overrides for new tickets, not retroactive edits to an open one.
  // Qty starts BLANK at mount on purpose — pre-populating it once
  // caused phantom-size accidents when traders re-entered the panel.
  const defaultExpiryModePref = useDisplaySettings((s) => s.defaultExpiryMode);
  const defaultPolyExpiryModePref = useDisplaySettings((s) => s.defaultPolyExpiryMode);
  const defaultContractsPref = useDisplaySettings((s) => s.defaultContracts);
  const quickSizesPref = useDisplaySettings((s) => s.quickSizes);
  // Maker-fee rebate fraction (0..1) for this ticket. 0 unless the org is
  // MM-enrolled and the MM Rebate toggle is on; live games rebate 100% of
  // the maker fee, pregame 50%. Only applied to maker (limit) orders.
  const makerRebateActive = useMakerRebateActive();
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: game.gameState === 'live' })
    : 0;
  // Raw/Fees toggle — reaches the Price row's quote only. Read here rather
  // than threaded as a prop so the ticket flips with the same click that flips
  // the ladders (the toggle lives in the page footer, not on this panel).
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);

  // Per-VENUE qty slots: Polymarket's 5-contract floor must never leak
  // into the Kalshi ticket (min 1) when the trader flips the venue tab.
  const [qtyByVenue, setQtyByVenue] = useState<Record<'kalshi' | 'polymarket', string>>(() => draft?.qtyByVenue ?? {
    kalshi: '',
    polymarket: '',
  });
  const qty = qtyByVenue[venue];
  const minQty = minQtyFor(venue);
  const setQty = useCallback(
    (next: string | ((prev: string) => string)) => {
      setQtyByVenue((prev) => ({
        ...prev,
        [venue]: typeof next === 'function' ? next(prev[venue]) : next,
      }));
    },
    [venue],
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<OrderResultLine | null>(null);
  const clearResult = useCallback(() => setResult(null), []);
  // Event-start availability controls whether a selected GTS instruction can
  // be submitted. It must never rewrite that instruction to GTC: the trader
  // has to make the unbounded-expiry choice explicitly.
  const gameStartMs = game.startTimeMs ?? null;
  const gtsAvailable = gameStartMs != null && gameStartMs > Date.now();

  // GTS sends the scheduled start as a FIXED venue expiry, resolved at submit
  // (see `expirationTsFrom` below) and never moved by the venue afterwards
  // (#510/#517 — a postponement leaves it behind); GTC ("Good to Close")
  // rests until cancel / market settle; CUSTOM auto-cancels at a user-
  // picked datetime. Initial mode comes from the user's preference even when
  // current event metadata cannot honor it; the resolver then fails closed
  // until the start returns or the trader explicitly chooses another mode.
  //
  // Per-VENUE expiry slots (same rationale as qtyByVenue): Polymarket's
  // CLOB auto-cancels a GTD at event start, so a Kalshi GTS default leaking
  // into the poly ticket silently kills resting maker orders at first pitch.
  const [expiryModeByVenue, setExpiryModeByVenue] = useState<
    Record<'kalshi' | 'polymarket', 'gts' | 'gtc' | 'custom'>
  >(() => draft?.expiryModeByVenue ?? {
    kalshi: defaultExpiryModePref,
    polymarket: defaultPolyExpiryModePref,
  });
  const expiryMode = expiryModeByVenue[venue];
  const setExpiryMode = useCallback(
    (mode: 'gts' | 'gtc' | 'custom') => {
      setExpiryModeByVenue((prev) => ({ ...prev, [venue]: mode }));
    },
    [venue],
  );
  // Native datetime-local input value, e.g. "2026-05-23T19:30". Empty
  // until the user types one; submit-time guard rejects empty Custom.
  const [customExpiry, setCustomExpiry] = useState(draft?.customExpiry ?? '');
  // The host keeps the slots across an unmount (Game Detail's US tab); only
  // their values move, never a venue or price.
  useEffect(() => {
    onDraftChange?.({ qtyByVenue, expiryModeByVenue, customExpiry });
  }, [onDraftChange, qtyByVenue, expiryModeByVenue, customExpiry]);

  // Good to Start for one-click: Game Detail's presses follow this ticket's
  // KALSHI slot (one-click is Kalshi-only), whichever venue tab is showing.
  // Withdrawn on unmount, so a page without this ticket falls back to the
  // Settings default the ticket would open on.
  const [oneClickExpiryOwner] = useState(nextTicketExpiryOwner);
  const kalshiExpiryMode = expiryModeByVenue.kalshi;
  useEffect(() => {
    if (!publishOneClickExpiry) return;
    publishTicketExpiry(oneClickExpiryOwner, kalshiExpiryMode, customExpiry);
  }, [publishOneClickExpiry, oneClickExpiryOwner, kalshiExpiryMode, customExpiry]);
  useEffect(() => {
    if (!publishOneClickExpiry) return;
    return () => clearTicketExpiry(oneClickExpiryOwner);
  }, [publishOneClickExpiry, oneClickExpiryOwner]);

  // ── Oscar overtake/blocker (permissioned, Kalshi + limit only) ─────────
  const oscarBlockerDefaultOnPref = useDisplaySettings((s) => s.oscarBlockerDefaultOn);
  const defaultBlockerContractsPref = useDisplaySettings((s) => s.defaultBlockerContracts);
  const oscarOwnerId = useAuthStore((s) => s.user?.id ?? '');
  const blockerSeed = Math.max(defaultBlockerContractsPref ?? defaultContractsPref, 1);
  const [oscarMode, setOscarMode] = useState(false);
  // #344 — read persisted storage ONCE. The previous `useRef(load(...))` form
  // re-read localStorage on every render and threw the result away; the lazy
  // state initializer runs exactly once and feeds the ref its seed. Behavior is
  // unchanged, including the pre-existing fact that the ref does not re-seed
  // when `oscarOwnerId` changes.
  const [initialOscarRetry] = useState<OscarRetryIdentity | null>(() =>
    loadOscarRetryIdentity(oscarOwnerId),
  );
  const oscarRetryRef = useRef<OscarRetryIdentity | null>(initialOscarRetry);
  const [blockerOn, setBlockerOn] = useState(oscarBlockerDefaultOnPref);
  const [blockerQty, setBlockerQty] = useState('');
  const oscarActive =
    oscarAccess &&
    isKalshiVenue &&
    orderType === 'limit' &&
    (oscarMode || oscarReleaseIntent != null);
  const oscarReleaseArmed = oscarActive && oscarReleaseIntent != null;
  // Entering oscar mode clears the ladder (mutually exclusive: the ladder is
  // same-side multi-rung; oscar is opposite-side two-leg) and seeds the
  // blocker qty if blank. Leaving keeps blockerQty for the next entry.
  const enterOscarMode = () => {
    onClearLegs();
    setOscarMode(true);
    setBlockerOn(oscarBlockerDefaultOnPref);
    setBlockerQty((prev) => (prev === '' ? String(blockerSeed) : prev));
    setResult(null);
  };
  const exitOscarMode = () => {
    setOscarMode(false);
    onClearOscarReleaseIntent();
    setResult(null);
  };
  // An armed release starts with replacement OFF. Releasing the old blocker
  // and attaching a new one are separate trader decisions; the normal
  // Overtake default still applies when entering ordinary Oscar mode.
  useLayoutEffect(() => {
    if (oscarReleaseIntent != null) {
      // Release intent is sibling-owned lifted state; this effect is the
      // established synchronization seam for ticket-local fields. Layout
      // timing keeps the first visible release frame aligned with the payload.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBlockerOn(false);
      setBlockerQty((prev) => (prev === '' ? String(blockerSeed) : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oscarReleaseIntent]);

  // Brief one-shot glow on the price input whenever something external
  // writes a new value (orderbook level click, prefill from tradeIntent).
  // Also opportunistically fills Qty with the user's default contracts on
  // each external price write — but ONLY when Qty is still blank.
  const [priceFlashing, setPriceFlashing] = useState(false);
  useEffect(() => {
    if (priceFlashKey === 0) return; // skip the mount-time tick
    setPriceFlashing(true);
    setQty((prev) => (prev === '' ? String(Math.max(defaultContractsPref, minQty)) : prev));
    // Two passes × 1.2s = 2.4s; small buffer so the class isn't yanked
    // before the CSS animation reaches its final frame.
    const t = setTimeout(() => setPriceFlashing(false), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceFlashKey]);

  // Venue-aware book + ticker. The form reads from whichever venue's
  // book the panel is currently pointing at.
  const book = ticketBook(game, venue, selectedSide);
  const ticker = book?.marketTicker ?? '';
  const exchangeIndex = isKalshiVenue ? book?.exchangeIndex : undefined;
  const exchangeIndexState = isKalshiVenue ? book?.exchangeIndexState : undefined;
  const marketStale = book?.freshness === 'stale';

  // Kalshi ticker for an arbitrary team — lets a ladder rung target the OTHER
  // team's market (both-teams placement) while the panel's own `ticker` tracks
  // the active team. Both-teams is Kalshi-only; Poly ladders stay single-market.
  const kalshiTickerForTeam = (team: TeamSide) =>
    (team === 'away' ? game.kalshi?.away : game.kalshi?.home)?.marketTicker ?? '';
  const teamCode = (team: TeamSide) => hasCfbEventSynthKey(game.key)
    ? '' : (team === 'away' ? game.awayCode : game.homeCode) ?? '';

  // Poly outcome tokens — required to actually place a Poly order via the
  // relay. resolvePolyOrderTokens handles both book layouts.
  const otherSideBook = !isKalshiVenue && !game.market
    ? (selectedSide === 'away' ? game.polymarket?.home : game.polymarket?.away)
    : undefined;
  const polyTokens = !isKalshiVenue
    ? resolvePolyOrderTokens(book, otherSideBook)
    : { yes: null, no: null };
  const polyYesTokenId = polyTokens.yes;
  const polyNoTokenId = polyTokens.no;
  const polyEffectiveTokenId =
    !isKalshiVenue ? (orderSide === 'yes' ? polyYesTokenId : polyNoTokenId) : null;
  const polyConditionId = !isKalshiVenue ? (book?.conditionId ?? null) : null;
  const {
    schedules: polyFeeSchedules,
    loading: polyFeeSchedulesLoading,
    error: polyFeeSchedulesError,
  } = usePolyFeeSchedules(polyConditionId ? [polyConditionId] : []);

  const bestYesHint = bestPriceCentsForOrderSide(book, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(book, 'no');

  // Minimum price increment in CENTS. Kalshi: the market's own priceRanges
  // win when present (banded helpers below); otherwise Kalshi's standard 1¢,
  // or 0.1¢ only when the live book already rests a sub-cent level (Dash Bug
  // 20096 — the old blanket 0.1¢ guess stepped linear_cent tickets to 50.1¢).
  //
  // Poly reads the CLOB's AUTHORITATIVE per-market minimum_tick_size, not
  // the engine frame: the frame's tickSize comes from Gamma and goes stale.
  // The frame is the fetch-failure fallback only.
  const [authoritativeTick, setAuthoritativeTick] = useState<string | null>(null);
  useEffect(() => {
    if (!polyConditionId) {
      setAuthoritativeTick(null);
      return;
    }
    let cancelled = false;
    void fetchMinTickSize(polyConditionId).then((tick) => {
      if (!cancelled) setAuthoritativeTick(tick);
    });
    return () => {
      cancelled = true;
    };
  }, [polyConditionId]);
  const kalshiBookYes = book?.yes;
  const kalshiBookNo = book?.no;
  const kalshiFallbackTick = useMemo(
    () =>
      kalshiFallbackTickCents(
        [...(kalshiBookYes ?? []), ...(kalshiBookNo ?? [])].map((level) => level.priceCents),
      ),
    [kalshiBookYes, kalshiBookNo],
  );
  const tickCents = isKalshiVenue
    ? kalshiFallbackTick
    : // Neither source known ⇒ keep the previous conservative default rather
      // than leaving the field unvalidated.
      (resolveTickCents(authoritativeTick, book?.tickSize) ??
      tickCentsFor('polymarket', book?.tickSize));
  const priceBounds = isKalshiVenue
    ? kalshiPriceBounds(book?.priceRanges, tickCents)
    : { min: tickCents, max: mirrorCents(tickCents) };
  const validateDraftPrice = useCallback(
    (raw: string): PriceInputResult =>
      isKalshiVenue
        ? validateKalshiPriceCentsInput(raw, book?.priceRanges, tickCents)
        : validatePriceCentsInput(raw, tickCents),
    [book?.priceRanges, isKalshiVenue, tickCents],
  );
  const numericDraft = Number.parseFloat(price);
  const previousPrice = stepKalshiPriceCents(
    Number.isFinite(numericDraft) ? numericDraft : 100,
    -1,
    isKalshiVenue ? book?.priceRanges : undefined,
    tickCents,
  );
  const nextPrice = stepKalshiPriceCents(
    Number.isFinite(numericDraft) ? numericDraft : 0,
    1,
    isKalshiVenue ? book?.priceRanges : undefined,
    tickCents,
  );
  const applyPriceStep = (next: number | null) => {
    if (next === null || orderType === 'market') return;
    onPriceChange(String(next));
    setResult(null);
  };

  const applyOrderSide = useCallback(
    (next: MarketSide) => {
      // #459 — re-clicking the already-active side is normally a convenient
      // top-of-book reseed. While a release is armed, however, that same click
      // would replace the protected take price without changing context or
      // triggering the destructive invalidation effect. Keep the armed order
      // instruction; the trader can still edit the price field explicitly.
      if (oscarReleaseIntent?.takeSide === next && orderSide === next) {
        setResult(null);
        return;
      }
      onOrderSideChange(next);
      setResult(null);
      if (game.market) { onPriceChange(''); return; }
      const hint = bestPriceCentsForOrderSide(book, next);
      if (hint !== undefined) onPriceChange(String(hint));
    },
    [book, game.market, onOrderSideChange, onPriceChange, orderSide, oscarReleaseIntent?.takeSide],
  );

  // The external price-write counter as of the previous commit. Compared in
  // the side-flip effect below, then advanced by the effect after it.
  const flashKeyAtLastCommit = useRef(priceFlashKey);

  // When the panel's side flips (main moneylines toggle pushed in, or the
  // user clicked our own BUY YES/NO above), refresh the price hint.
  useEffect(() => {
    setResult(null);
    // #459 — arming a release intentionally flips to the blocker's opposite
    // side and writes the protected take level in the same commit. That armed
    // price is an order instruction, not a generic side-toggle hint; replacing
    // it with top-of-book here made the banner promise one price while the
    // preview and POST submitted another.
    if (oscarReleaseIntent?.takeSide === orderSide) return;
    // #1027 — a price click that also flips the side (the first click into a
    // market while the page is on the other side) writes the clicked level in
    // the same commit. That level is the trader's instruction; top-of-book
    // must not replace it.
    if (flashKeyAtLastCommit.current !== priceFlashKey) return;
    if (game.market) return;
    const hint = bestPriceCentsForOrderSide(book, orderSide);
    if (hint !== undefined) onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSide]);
  // Declared after the side-flip effect so it runs after it in each commit.
  useEffect(() => {
    flashKeyAtLastCommit.current = priceFlashKey;
  });

  useEffect(() => {
    if (game.market) return;
    const hint = bestPriceCentsForOrderSide(book, orderSide);
    if (hint === undefined) return;
    if (price === '') onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book, orderSide]);

  const submit = useCallback(async () => {
    const diagnostic = isKalshiVenue ? null : beginPolyAttempt(polyEffectiveTokenId);
    // #344 — the body is an inner function so `bumpHeadroom()` runs after it in
    // EXACTLY the cases the old `handleSubmit().then(() => bumpHeadroom())` did:
    // every path that returns normally, including the early validation bails,
    // and no path that throws. Putting the bump in a `finally`, or only on the
    // success path, would both be wrong.
    const run = async () => {
    // The form stays mounted while the account connects; the button gate is
    // presentation, this is the submit path's own check.
    if (isKalshiVenue && useAccountStore.getState().status !== 'connected') {
      setResult({ ok: false, msg: 'Kalshi account is not connected yet' });
      return;
    }
    if (game.market && orderType !== 'limit') {
      setResult({ ok: false, msg: 'Futures use explicit limit orders.' });
      return;
    }
    if (game.validateCurrent && !game.validateCurrent()) {
      setResult({ ok: false, msg: 'This market changed or is unavailable. Reopen it before placing an order.' });
      return;
    }
    // Shared decimal parse + (0,100) + tick-alignment validator (DNL-006 —
    // one helper across order form and edit/close modals). parseInt here
    // used to silently truncate sub-cent prices to whole cents.
    //
    // These locals are NOT the render-scope priceCents/count: on invalid input
    // they are NaN where those are 0, and the guards below test `isNaN(count)`.
    const priceCheck = validateDraftPrice(price);
    const priceCents = priceCheck.ok ? priceCheck.priceCents : NaN;
    const count = parseInt(qty, 10);
    const isMarket = orderType === 'market';

    if (!ticker) {
      setResult({ ok: false, msg: `No ${isKalshiVenue ? 'Kalshi' : 'Polymarket'} market found` });
      return;
    }
    if (marketStale) {
      setResult({ ok: false, msg: 'This market is stale; trading is unavailable until the feed resumes' });
      return;
    }

    // ── Multi-price ladder (Kalshi, limit): place each valid rung as its own
    // resting limit order, paced ≥MIN_ORDER_SPACING_MS apart so one click never
    // bursts past ~4 orders/sec on the shared Kalshi account bucket. Recomputes
    // the ladder condition inline; it must stay in step with `ladderActive`.
    const isLadder = isKalshiVenue && orderType === 'limit' && extraLegs.length > 0;
    if (isLadder) {
      const { legs, invalidCount } = resolveLadderPlacements({
        selectedSide,
        price,
        qty,
        extraLegs,
        tickerForTeam: (team) =>
          (team === 'away' ? game.kalshi?.away : game.kalshi?.home)?.marketTicker ?? '',
        exchangeIndexForTeam: (team) =>
          (team === 'away' ? game.kalshi?.away : game.kalshi?.home)?.exchangeIndex,
      });
      if (legs.length === 0) {
        setResult({ ok: false, msg: 'No valid rows to place — each needs a market, price 1–99¢, qty ≥ 1' });
        return;
      }
      // Expiry resolved once; every rung shares side / type / expiry.
      const ladderExpiry = resolveExpiry({
        expiryMode,
        gameStartMs,
        customExpiry,
        nowMs: Date.now(),
      });
      if (!ladderExpiry.ok) {
        setResult({ ok: false, msg: ladderExpiry.error });
        return;
      }
      const ladderExpTime = expirationTimeFrom(ladderExpiry.expirationMs);
      const footballIdentity = hasCfbEventSynthKey(game.key)
        ? `${cfbEventOutcomeLabel(game, orderSide)} · ${ticker}` : null;
      const batchSuffix = footballIdentity ? ` · ${footballIdentity}` : '';

      setSubmitting(true);
      // In flight from the first request: the slot says what is being placed
      // and where, and says nothing about acceptance until each leg answers.
      setResult(
        placementBatchPendingLine(
          legs.length,
          orderSide,
          footballIdentity ?? matchupLabel({ awayCode: game.awayCode, homeCode: game.homeCode }) ?? ticker,
        ),
      );
      let placed = 0;
      let basePlaced = false;
      const placedExtraIds: string[] = [];
      const failures: string[] = [];
      // Legs whose outcome the client never learned. Not failures: the venue
      // may hold them, so they stay in the form and are never called failed.
      const unverified: string[] = [];
      // #1229: rungs that partly filled before self-trade prevention cancelled
      // the rest. They landed (the fills are real) but nothing rests.
      const partials: string[] = [];
      try {
        for (let i = 0; i < legs.length; i += 1) {
          const leg = legs[i];
          const startedAt = performance.now();
          try {
            const placedLeg = await placeOrder(
              buildLadderLegPayload({
                ticker: leg.ticker,
                exchangeIndex: leg.exchangeIndex,
                orderSide,
                priceCents: leg.priceCents,
                qty: leg.qty,
                expirationTime: ladderExpTime,
                goodToStartStartMs: expiryMode === 'gts' ? ladderExpiry.expirationMs : undefined,
              }),
            );
            placed += 1;
            if (leg.id === null) basePlaced = true;
            else placedExtraIds.push(leg.id);
            if (placedLeg.selfTradeCancel) {
              partials.push(
                `${leg.priceCents}¢ filled ${placedLeg.selfTradeCancel.filled} of ${placedLeg.selfTradeCancel.requested}, ` +
                'remainder cancelled by self-trade protection',
              );
            }
          } catch (err) {
            const code = footballIdentity ? `${orderSide.toUpperCase()} ${leg.ticker}`
              : leg.team === 'away' ? game.awayCode : game.homeCode;
            const legId = `${code} ${leg.priceCents}¢×${leg.qty}`;
            if (err instanceof OrderSubmissionUnknownError) unverified.push(legId);
            else failures.push(`${legId}: ${err instanceof Error ? err.message : 'failed'}`);
          }
          // Pace to ≤4/sec — wait out the remainder of the spacing window before
          // the next submission (no wait after the final leg).
          if (i < legs.length - 1) {
            const elapsed = performance.now() - startedAt;
            if (elapsed < MIN_ORDER_SPACING_MS) {
              await new Promise((resolve) => setTimeout(resolve, MIN_ORDER_SPACING_MS - elapsed));
            }
          }
        }
      } finally {
        setSubmitting(false);
      }
      // Drop the rungs that landed; keep failed + invalid ones for a retry.
      if (basePlaced) setQty('');
      placedExtraIds.forEach((id) => onRemoveLeg(id));
      const skipped = invalidCount > 0 ? ` · ${invalidCount} skipped (invalid)` : '';
      if (unverified.length > 0) {
        // The ambiguous legs lead: a trader who reads "failed" retries, and a
        // retry here can double live exposure. Definitive failures follow.
        const failedPart =
          failures.length > 0
            ? ` · failed ${failures[0]}${failures.length > 1 ? ` (+${failures.length - 1} more)` : ''}`
            : '';
        setResult({
          ok: false,
          severity: 'critical',
          msg:
            `Placed ${placed}/${legs.length} · ${ORDER_UNCONFIRMED_LABEL} ${unverified.join(', ')}: ` +
            `outcome still being verified — check Open Orders before retrying${failedPart}${skipped}${batchSuffix}`,
        });
      } else if (failures.length === 0 && partials.length > 0) {
        setResult({
          ok: false,
          severity: 'warning',
          msg: `Placed ${placed} ${orderSide.toUpperCase()} order${placed === 1 ? '' : 's'} · ${partials.join('; ')}${skipped}${batchSuffix}`,
          reference: SELF_TRADE_CANCEL_REFERENCE,
        });
      } else if (failures.length === 0) {
        setResult({
          ok: true,
          msg: `Placed ${placed} ${orderSide.toUpperCase()} order${placed === 1 ? '' : 's'}${skipped}${batchSuffix}`,
        });
      } else {
        const more = failures.length > 1 ? ` (+${failures.length - 1} more)` : '';
        setResult({
          ok: false,
          msg: `Placed ${placed}/${legs.length} · failed ${failures[0]}${more}${skipped}${batchSuffix}`,
        });
      }
      return;
    }

    if (isNaN(count) || count < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }
    // Polymarket rejects sub-floor orders at the CLOB; fail it here instead of
    // burning the round-trip (Kalshi's minimum stays 1). POLY_MIN_ORDER_CONTRACTS
    // is the ONLY submit-time authority — a hardcoded `count < 5` stood here
    // unreachable until #567. It would have become the binding rule silently,
    // but only on a LOWERED floor: raising the constant keeps this guard first.
    if (!isKalshiVenue && count < POLY_MIN_ORDER_CONTRACTS) {
      setResult({ ok: false, msg: `Polymarket's minimum order is ${POLY_MIN_ORDER_CONTRACTS} contracts` });
      return;
    }
    // Hard-reject with the reason named (misaligned tick, out of range) so
    // the trader can correct — a silently snapped price is a real-money
    // surprise. Whole-cent markets are never blocked (tick fallback 1¢).
    if (!isMarket && !priceCheck.ok) {
      setResult({ ok: false, msg: priceCheck.error });
      return;
    }

    // Poly-specific guards — token must resolve (some Poly markets
    // don't expose a NO outcome via hydra yet), and market orders
    // aren't supported through the relay yet.
    if (!isKalshiVenue && !polyEffectiveTokenId) {
      setResult({
        ok: false,
        msg: `No Polymarket outcome token available for ${orderSide.toUpperCase()} on this market.`,
      });
      return;
    }
    if (!isKalshiVenue && isMarket) {
      setResult({
        ok: false,
        msg: 'Polymarket market orders are not yet supported from this ticket.',
      });
      return;
    }

    // Resolve expiry as ms-since-epoch once, then derive the venue-specific
    // wire format below (Kalshi wants ISO 8601, Polymarket wants unix seconds).
    // Skipped for market orders since they fill immediately.
    let expirationMs: number | undefined;
    if (!isMarket) {
      const expiry = resolveExpiry({ expiryMode, gameStartMs, customExpiry, nowMs: Date.now() });
      if (!expiry.ok) {
        setResult({ ok: false, msg: expiry.error });
        return;
      }
      expirationMs = expiry.expirationMs;
    }
    const expirationTime = expirationTimeFrom(expirationMs);
    const expirationTs = expirationTsFrom(expirationMs);

    // The attempt's identity is fixed BEFORE the request, from the values
    // actually sent, so the pending, success and failure lines all name the
    // same order even if the form moves on under a slow response. Polymarket
    // keeps its own typed outcome producers (#549/#556) and no pending line.
    const attempt = describeOrderAttempt({
      count,
      side: orderSide,
      priceCents: isMarket ? null : priceCents,
      teamCode: hasCfbEventSynthKey(game.key) ? null
        : (selectedSide === 'away' ? game.awayCode : game.homeCode) ?? null,
      matchup: hasCfbEventSynthKey(game.key)
        ? `${cfbEventOutcomeLabel(game, orderSide)} · ${ticker}`
        : game.market?.title ?? matchupLabel({ awayCode: game.awayCode, homeCode: game.homeCode }),
      ticker,
    });
    setSubmitting(true);
    setResult(isKalshiVenue ? placementPendingLine(attempt) : null);
    try {
      if (isKalshiVenue) {
        const placed = await placeOrder(
          buildKalshiOrderPayload({
            ticker,
            exchangeIndex,
            orderSide,
            orderType,
            count,
            priceCents,
            isMarket,
            expirationTime,
            goodToStartStartMs: expiryMode === 'gts' ? expirationMs : undefined,
          }),
        );
        // #1229: a partial self-trade cancel is not "Order placed". The
        // zero-fill cancel threw and reads as a failure line below.
        setResult(
          placed.selfTradeCancel
            ? placementSelfTradePartialLine(attempt, placed.selfTradeCancel)
            : placementSuccessLine(attempt),
        );
      } else {
        // Never let an unrecognized venue take the international write path.
        if (venue !== 'polymarket') unsupportedVenue(venue, 'Place order');
        const out = await placePolyOrderViaRelay(
          buildPolyOrderPayload({
            tokenId: polyEffectiveTokenId!,
            priceCents,
            contracts: count,
            negRisk: book?.negRisk,
            tickSize: book?.tickSize,
            expirationTs,
          }),
          diagnostic,
        );
        // The venue answer's tone, by the Kalshi rule: accepted -> placed,
        // definite refusal -> rejected, unknown -> none (the line says to look).
        ringPolyPlacement(polyPlacementOutcome(out));
        if (!out.ok) {
          let msg: string;
          if (
            out.code === 'LIMIT_MAX_CONTRACTS' ||
            out.code === 'LIMIT_MAX_POSITIONS' ||
            out.code === 'LIMIT_SPORT_NOT_ALLOWED' ||
            out.code === 'LIMIT_NON_SPORT_DISABLED' ||
            out.code === 'LIMIT_POLYMARKET_DISABLED' ||
            out.code === 'LIMIT_MARKET_SPORT_UNKNOWN'
          ) {
            msg = describeLimitRejection(out.code, out.error);
          } else if (out.code === 'POLYMARKET_GEOBLOCK') {
            msg = explainPolyError(out.error);
          } else if (
            out.code === 'POLY_MARKET_METADATA_UNAVAILABLE' ||
            out.code === 'POLY_ORDER_PREP_FAILED' ||
            out.code === 'POLY_ORDER_STATE_UNKNOWN'
          ) {
            // #549 — an order-ATTEMPT outcome, not a config problem. Whether it
            // may claim "no order was submitted" was decided at the relay from
            // the observed submission phase, so the copy is chosen by code
            // alone and never softened or hardened here.
            //
            // #556 — and so is the TONE. Both come from one producer keyed on
            // the same code, so an amber banner can never end up carrying the
            // ORDER UNCONFIRMED sentence. It is the only branch in THIS chain
            // that carries a severity — the others fall through to the shared
            // `{ ok: false, msg }` and keep their red fallback, because a bare
            // failure cannot prove whether the order reached the venue. (The
            // Oscar path sets one too, from its own per-leg evidence.)
            setResult(describePolyOrderOutcomeLine(out.code));
            return;
          } else if (out.code) {
            msg = describePolyRelayUnavailable(out.code);
          } else {
            // No code: a transport failure before the relay answered, or a
            // pre-#549 shape. `explainPolyError` decodes known upstream
            // signatures and otherwise passes the string through.
            msg = explainPolyError(out.error);
          }
          setResult({ ok: false, msg });
          return;
        }
        setResult({ ok: true, msg: `Polymarket order placed: ${count} ${orderSide.toUpperCase()} @ ${priceCents}¢` });
      }
      setQty('');
    } catch (err) {
      setResult(
        isKalshiVenue
          ? placementFailureLine(attempt, err)
          : { ok: false, msg: err instanceof Error ? err.message : 'Order failed' },
      );
    } finally {
      setSubmitting(false);
    }
    };

    try { await run(); } finally { diagnostic?.finish(); }
    bumpHeadroom();
  }, [
    ticker,
    exchangeIndex,
    orderSide,
    orderType,
    price,
    qty,
    placeOrder,
    expiryMode,
    gameStartMs,
    customExpiry,
    isKalshiVenue,
    venue,
    polyEffectiveTokenId,
    book,
    extraLegs,
    onRemoveLeg,
    selectedSide,
    game,
    validateDraftPrice,
    marketStale,
    setQty,
  ]);

  // ── Oscar sequence receipt (#302) — durable evidence, read-only.
  // Gated on access + venue + ticker rather than on `oscarActive`: the receipt
  // has to survive leaving Overtake mode, a page refresh, and a reopened game.
  const {
    receipt: oscarReceipt,
    loading: oscarReceiptLoading,
    error: oscarReceiptError,
    loadExact: loadExactOscarReceipt,
    refresh: refreshOscarReceipt,
  } = useOscarReceipt({
    ticker: ticker || null,
    enabled: oscarAccess && isKalshiVenue,
  });
  const showOscarReceipt = oscarAccess && isKalshiVenue && !!ticker;

  // ── One-click receipts (#314) — durable evidence, read-only.
  // Gated on access + venue + ticker, NEVER on `armed`: the session-local ack
  // in oneClickStore is wiped by arm/disarm and reset on every page load.
  const oneClickAccess = useOneClickAccess();
  const {
    receipts: oneClickReceipts,
    loading: oneClickReceiptsLoading,
    error: oneClickReceiptsError,
    refresh: refreshOneClickReceipts,
  } = useOneClickReceipts({
    ticker: ticker || null,
    enabled: oneClickAccess && isKalshiVenue,
  });
  const showOneClickReceipts = oneClickAccess && isKalshiVenue && !!ticker;
  const oneClickReceiptsPending =
    useGrantSlot('oneClickAccess') === 'pending' && isKalshiVenue && !!ticker;

  // A fresh receipt should appear without the trader hitting Refresh.
  // `pending` is pre-flight and `throttled` is a client-side drop that never
  // reaches the relay, so neither can have produced an audit row. Keyed on the
  // monotonic seq so two consecutive orders with the same outcome each refetch.
  //
  // NOTE: this is a derived selector, not a store field. `unverified` is
  // included because an ambiguous submission writes a durable unknown receipt
  // that the trader should see without manually pressing Refresh. `partial`
  // (#724) is a two-sided press where one leg reached the venue: its resting
  // leg must appear on the receipts like any other accepted order.
  const oneClickReceiptRefreshAckSeq = useOneClickStore((s) =>
    oneClickReceiptRefreshSeq(s.lastAck),
  );
  useEffect(() => {
    if (oneClickReceiptRefreshAckSeq == null || !showOneClickReceipts) return;
    void refreshOneClickReceipts();
  }, [oneClickReceiptRefreshAckSeq, showOneClickReceipts, refreshOneClickReceipts]);
  // #1025 — and a passive order that fills after its own ack, without Refresh.
  useReceiptFillReread({
    ticker: ticker || null,
    enabled: showOneClickReceipts,
    receipts: oneClickReceipts,
    loading: oneClickReceiptsLoading,
    refresh: refreshOneClickReceipts,
  });

  // #344 — ONE preview, shared by the fire path and the JSX. Both call sites
  // passed byte-identical arguments before the extraction; computing it twice
  // per render was pure duplication.
  const oscarPreview = useMemo(
    () =>
      computeOscarPreview({
        side: orderSide,
        takePriceRaw: price,
        takeQtyRaw: qty,
        blockerOn,
        blockerQtyRaw: blockerQty,
        cancelOrderId: oscarReleaseIntent?.cancelOrderId ?? null,
      }),
    [orderSide, price, qty, blockerOn, blockerQty, oscarReleaseIntent?.cancelOrderId],
  );

  // ── Oscar fire: one relay round-trip executes the whole sequence
  // (cancel? → take → blocker) — POST /api/kalshi/order-sequence. The relay
  // recomputes the blocker price server-side; the preview here is display.
  const fireOscar = useCallback(async () => {
    // Same wrapper rationale as `submit` above: the bump follows every normal
    // completion, including the pre-flight bails, and no throw.
    const run = async () => {
    if (!oscarAccess) {
      setResult({ ok: false, msg: 'Overtake access is not confirmed' });
      return;
    }
    if (useAccountStore.getState().status !== 'connected') {
      setResult({ ok: false, msg: 'Kalshi account is not connected yet' });
      return;
    }
    if (!ticker) {
      setResult({ ok: false, msg: 'No Kalshi market found' });
      return;
    }
    if (marketStale) {
      setResult({ ok: false, msg: 'This market is stale; trading is unavailable until the feed resumes' });
      return;
    }
    const preview = oscarPreview;
    if (!preview.ok) {
      setResult({ ok: false, msg: preview.error });
      return;
    }
    // Same expiry resolution as the single-order path (limit-only surface).
    const expiry = resolveExpiry({ expiryMode, gameStartMs, customExpiry, nowMs: Date.now() });
    if (!expiry.ok) {
      setResult({ ok: false, msg: expiry.error });
      return;
    }
    const expirationMs = expiry.expirationMs;

    const takeLeg = preview.legs.find((l) => l.role === 'take')!;
    const blockerLeg = preview.legs.find((l) => l.role === 'blocker');
    // #302 — the sequence id this attempt actually POSTed, captured so the
    // durable receipt can be re-read for THIS fire rather than for whatever
    // happens to be the latest sequence on the ticker. Stays null when we
    // bail before minting one.
    let attemptedSequenceId: string | null = null;
    setSubmitting(true);
    // Keep the overtake surface up after the fire even when the release
    // intent (which forced it on) is cleared below.
    setOscarMode(true);
    setResult(null);
    try {
      const sequenceInput = buildOscarSequenceInput({
        ticker,
        exchangeIndex,
        orderSide,
        takeLeg: { priceCents: takeLeg.priceCents, qty: takeLeg.qty },
        blockerLeg: blockerLeg ? { qty: blockerLeg.qty } : undefined,
        expirationMs,
        cancelOrderId: oscarReleaseIntent?.cancelOrderId ?? null,
      });
      const previousRetryIdentity = oscarRetryRef.current;
      const retryIdentity = oscarRetryIdentity(
        previousRetryIdentity,
        sequenceInput,
        () => crypto.randomUUID(),
        oscarOwnerId,
      );
      oscarRetryRef.current = retryIdentity;
      attemptedSequenceId = retryIdentity.sequenceId;
      saveOscarRetryIdentity(retryIdentity);
      const seq = await placeOrderSequence({
        ...sequenceInput,
        sequenceId: retryIdentity.sequenceId,
        ...(previousRetryIdentity === retryIdentity ? { retry: true } : {}),
      });
      setResult(describeOscarSequenceResult(seq));
      if (seq.ok) {
        oscarRetryRef.current = null;
        saveOscarRetryIdentity(null);
        setQty('');
        onClearOscarReleaseIntent();
      } else if (seq.legs.find((l) => l.role === 'cancel')?.ok) {
        // The old blocker is gone either way — a re-fire must not try to
        // cancel it again (it would alreadyGone-continue, but the armed
        // banner would lie about what the next click does).
        onClearOscarReleaseIntent();
      }
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Sequence failed' });
    } finally {
      setSubmitting(false);
      // #302 — refresh the durable receipt for the sequence we just attempted.
      // Deliberately AFTER setSubmitting and deliberately NOT awaited: this is
      // an isolated read, so a slow or failed audit lookup must not delay the
      // submission reset, mask the POST outcome, change whether the next fire
      // is a retry, or delay the headroom bump below.
      // loadExact owns its own loading/error state and never rejects.
      if (attemptedSequenceId != null) void loadExactOscarReceipt(attemptedSequenceId);
    }
    };

    await run();
    bumpHeadroom();
  }, [
    oscarAccess,
    ticker,
    exchangeIndex,
    orderSide,
    oscarPreview,
    expiryMode,
    gameStartMs,
    customExpiry,
    placeOrderSequence,
    oscarReleaseIntent,
    onClearOscarReleaseIntent,
    oscarOwnerId,
    setQty,
    loadExactOscarReceipt,
    marketStale,
  ]);

  // Hydra-sourced per-series fee schedule. `feeEntry` is undefined while the
  // initial fetch is in flight; the economics render "—" rather than guessing.
  const feeEntry = useFeeFor(ticker);

  // ── Pre-trade limit preview (advisory; relay is the authoritative gate)
  const openPositionKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    for (const p of kalshiPositions ?? []) {
      if (p.position > 0) keys.add(positionKey(p.ticker, p.side));
    }
    return keys;
  }, [kalshiPositions]);

  // Preview economics MUST use the same canonical decimal the submit path
  // sends (DNL round-3 finding 5): parseInt previewed a 58.25¢ order as 58¢,
  // so traders approved economics that differed from the order placed.
  const previewPriceCheck = validateDraftPrice(price);
  const priceCents = previewPriceCheck.ok ? previewPriceCheck.priceCents : 0;
  const count = parseInt(qty, 10) || 0;

  // Ladder mode: Kalshi + limit + at least one extra rung.
  const ladderActive = isKalshiVenue && orderType === 'limit' && extraLegs.length > 0;
  const ladder = ladderActive ? buildLadderLegs({ price, qty }, extraLegs) : null;
  const ladderSummary = ladder ? summarizeLegs(ladder.valid) : null;
  // Contracts the cap-preview should consider: the whole ladder when active,
  // else the single base row — plus the blocker leg in oscar mode.
  const oscarBlockerCount =
    oscarActive && blockerOn ? parseInt(blockerQty, 10) || 0 : 0;
  const effectiveCount =
    (ladderActive ? (ladderSummary?.contracts ?? 0) : count) + oscarBlockerCount;
  // Per-team contract split for the ladder summary (both-teams placement).
  const ladderTeamSplit = ladderActive
    ? (() => {
        const rows = [
          { team: selectedSide, price, qty },
          ...extraLegs.map((l) => ({ team: l.team, price: l.price, qty: l.qty })),
        ];
        const acc: Record<'away' | 'home', number> = { away: 0, home: 0 };
        for (const r of rows) {
          const v = validateLeg({ price: r.price, qty: r.qty });
          if (v.ok && kalshiTickerForTeam(r.team)) acc[r.team] += v.parsed.qty;
        }
        return acc;
      })()
    : null;

  const previewBreach =
    headroom && effectiveCount > 0 && ticker
      ? previewOrder(
          { maxContracts: headroom.maxContracts, maxPositions: headroom.maxPositions },
          {
            openContracts: headroom.openContracts,
            openPositions: headroom.openPositions,
            intentContracts: headroom.intentContracts,
            openPositionKeys,
          },
          {
            action: 'buy',
            ticker: isKalshiVenue ? ticker : (polyEffectiveTokenId ?? ticker),
            side: orderSide,
            count: effectiveCount,
          },
        ).breach
      : null;
  // Polymarket can't reliably check position-key novelty at the client
  // (tokenId vs conditionId), so suppress that-specific advisory breach for
  // poly. The relay enforces the contracts cap on poly, which matters more.
  const effectiveBreach =
    previewBreach && !isKalshiVenue && previewBreach.rule === 'LIMIT_MAX_POSITIONS'
      ? null
      : previewBreach;

  const { disabled: submitDisabled } = submitGate({
    submitting,
    effectiveBreach,
    isKalshiVenue,
    count,
    ladderActive,
    ladderLegs: ladderSummary?.legs ?? 0,
    qty,
    orderType,
    price,
  });
  const kalshiAccountAbsent = credentialsStatus === 'checked' && credentialCount === 0 && status !== 'connected';
  const kalshiAccountReason = !isKalshiVenue || status === 'connected' ? null
    // Short enough for the heading's one reserved line at the narrowest rail.
    : credentialsStatus === 'error' ? "Couldn't check Kalshi keys."
      : status === 'connecting' ? 'Connecting to Kalshi…'
        : status === 'error' ? 'Kalshi account unavailable.'
          : credentialsStatus === 'checked' ? 'Kalshi not connected — connect in Settings.'
            : 'Checking Kalshi account…';
  const gatedSubmitDisabled = submitDisabled || marketStale || kalshiAccountReason != null;

  const bookRole = limitOrderRole(book, orderSide, priceCents);
  const polyRole = isKalshiVenue ? null : bookRole;
  const polyScheduleKnown = polyConditionId ? polyFeeSchedules.has(polyConditionId) : false;
  const polySchedule = polyConditionId ? polyFeeSchedules.get(polyConditionId) : undefined;
  // Fee perspective for the Price row's quote (#535) — read off the BOOK, not
  // off `orderType`. An ask click lands here as a marketable LIMIT (see the
  // orderbook click handler in GameDetailPanel), so an orderType rule would
  // quote maker odds against a ladder ask row quoting taker, which is the very
  // disagreement this is meant to remove.
  //
  // A market order always crosses. A limit is whatever the book says; with no
  // opposing depth we can't PROVE it crosses, so it's assumed to rest — the
  // same assumption `economics.isMaker` makes for the fee estimate.
  const quotePerspective: FeePerspective =
    orderType === 'market' ? 'taker' : (bookRole ?? 'maker');
  const economics = computeOrderEntryEconomics({
    isKalshiVenue,
    orderType,
    priceCents,
    count,
    feeEntry,
    makerRebateFraction,
    feeAdjusted,
    quotePerspective,
    polyRole,
    polyConditionId,
    polyScheduleKnown,
    polySchedule,
    polyFeeSchedulesLoading,
    polyFeeSchedulesError,
  });
  const ladderTotals = computeLadderTotals({
    ladder,
    ladderSummary,
    feeRate: economics.feeRate,
    isMaker: economics.isMaker,
    makerRebateFraction,
  });

  return {
    isKalshiVenue,
    ticker,
    exchangeIndex,
    exchangeIndexState,
    teamCode,

    accountConnected: status === 'connected',
    kalshiAccountAbsent,
    kalshiAccountReason,
    retryKalshiAccount: isKalshiVenue && credentialsStatus === 'error'
      ? () => { void useAccountStore.getState().checkCredentials(); }
      : null,
    hasTicker: !!ticker,
    hasPolyTokens: !!polyYesTokenId || !!polyNoTokenId,

    priceBounds,
    previousPrice,
    nextPrice,
    bestYesHint,
    bestNoHint,
    priceFlashing,
    priceCents,

    qty,
    minQty,
    count,
    defaultContracts: defaultContractsPref,
    quickSizes: quickSizesPref,

    expiryMode,
    gtsAvailable,
    customExpiry,

    feeLabel: economics.feeLabel,
    feeTitle: economics.feeTitle,
    totalCostLabel: economics.totalCostLabel,
    maxPayoutLabel: economics.maxPayoutLabel,
    maxProfit: economics.maxProfit,
    maxProfitLabel: economics.maxProfitLabel,
    effectiveOddsLabel: economics.effectiveOddsLabel,
    priceQuote: economics.priceQuote,

    effectiveBreach,
    breachLine: breachLineFor(effectiveBreach, input.limitsReadsFailing === true, limitsReadAtMs),
    marketStale,
    submitDisabled: gatedSubmitDisabled,
    submitting,

    ladderActive,
    ladderSummary,
    ladderTeamSplit,
    ladderTotals,

    oscarAccess,
    oscarRowShown,
    oscarActive,
    oscarReleaseArmed,
    blockerOn,
    blockerQty,
    oscarPreview,

    showOscarReceipt,
    showOneClickReceipts,
    oneClickReceiptsPending,
    oscarReceipt,
    oscarReceiptLoading,
    oscarReceiptError,
    refreshOscarReceipt,
    oneClickReceipts,
    oneClickReceiptsLoading,
    oneClickReceiptsError,
    refreshOneClickReceipts,

    result,

    clearResult,
    setQty,
    setCustomExpiry,
    setExpiryMode,
    setBlockerOn,
    setBlockerQty,
    enterOscarMode,
    exitOscarMode,
    applyPriceStep,
    applyOrderSide,
    submit,
    fireOscar,
  };
}

/** The ack sequence that should refetch the one-click receipts, or null.
 *  Every outcome that can have written an audit row refetches: accepted,
 *  rejected, unverified, and a partial two-sided press (#724), whose resting
 *  leg must appear like any other accepted order. `pending` is pre-flight and
 *  `throttled` never reaches the relay. */
export function oneClickReceiptRefreshSeq(
  ack: { status: string; seq: number } | null,
): number | null {
  if (ack == null) return null;
  return ack.status === 'accepted' ||
    ack.status === 'rejected' ||
    ack.status === 'unverified' ||
    ack.status === 'partial'
    ? ack.seq
    : null;
}
